import React from 'react'

import { useForm } from 'react-hook-form'
import { faEnvelope, faLock } from '@fortawesome/pro-regular-svg-icons'

import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { Content, Form } from 'ui'
import { RequiredEmail, RequiredString } from 'validations'
import { tryCatch } from 'utils'

import { FormProps } from './types'

const LoginSchema = Yup.object().shape({
  email: RequiredEmail(),
  password: RequiredString().min(7),
})

export interface LoginFormValues {
  [key: string]: string | undefined
  email?: string | undefined
  password?: string | undefined
}

interface LoginFormProps extends FormProps<LoginFormValues> {}

/**
 * A simple login form for existing users.
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit: externalSubmitHandler,
}) => {
  const { handleSubmit, register, errors, setError, formState, reset } =
    useForm<LoginFormValues>({
      mode: 'onBlur',
      resolver: yupResolver(LoginSchema),
    })

  const onSubmit = handleSubmit(async (data: LoginFormValues) => {
    await tryCatch(
      async () => {
        const errors = (await externalSubmitHandler(data)) ?? []
        errors?.length
          ? errors.forEach(({ path, message }) =>
              setError(path as any, { message })
            )
          : reset({})
      },
      (error: Error) => {
        setError('_', error)
      }
    )
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col w-full">
      <Form.Fields className="flex-col" errors={errors} register={register}>
        <Form.TextField
          disabled={formState.isSubmitting}
          className="w-full"
          icon={faEnvelope}
          name="email"
          label="Email Address"
          type="text"
          required
        />
        <Form.TextField
          disabled={formState.isSubmitting}
          className="w-full"
          icon={faLock}
          name="password"
          label="Password"
          type="password"
          required
        />
        <div className="flex flex-row flex-wrap md:w-1/2 w-full mt-1 ml-auto">
          <Content.ActionButton
            label="Login"
            type="submit"
            className="ml-auto self-align-right"
            loading={formState.isSubmitting}
          />
        </div>
      </Form.Fields>
      <Form.ErrorComponent error={(errors as any)['_']?.message} />
    </form>
  )
}
